import { Anchor, Button } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { Avatar, ContentHeader, Icon, MetaHeader, toastNotifications, variants, useAsyncActionWithToast } from '@uag/react-core';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { DetailUserModel, TenantLoginProviderType } from 'api/v3/models';
import { getGetCurrentUserDetailsQueryKey, useUploadCurrentUserImage, useUploadCurrentUserTitleImage } from 'api/v3/user/user';

type ProfileHeadProps = {
    user: DetailUserModel;
};

export const ProfileHead = ({ user }: ProfileHeadProps) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const { mutateAsync: uploadCurrentUserImage } = useUploadCurrentUserImage();

    const { mutateAsync: uploadCurrentUserTitleImage } = useUploadCurrentUserTitleImage();

    const isActiveDirectoryUser = user.loginProviderType === TenantLoginProviderType.AzureAD;
    const { profilePictureUris, displayName, titleImageUri } = user;

    const imageSource = profilePictureUris.find((picture) => picture.size === 'Large')?.uri || profilePictureUris[0].uri;
    const titleImageSource = titleImageUri ?? undefined;

    const { run } = useAsyncActionWithToast({
        action: uploadCurrentUserImage,
        successMessageKey: 'dataSaved',
        successMessageParameters: () => ({ data: t('titleImage') }),
        errorMessageKey: 'dataNotSaved',
    });

    const handleImageSelected = async (file: File | null | undefined) => {
        if (!file) {
            return;
        }
        await run({
            data: { File: file },
        });
        await queryClient.invalidateQueries({ queryKey: getGetCurrentUserDetailsQueryKey() });
    };

    const handleTitleImageSelected = async (file: File | null | undefined) => {
        if (!file) {
            return;
        }

        try {
            await uploadCurrentUserTitleImage({ data: { File: file } });
            await queryClient.invalidateQueries({ queryKey: getGetCurrentUserDetailsQueryKey() });
            toastNotifications.success({
                title: t('success'),
                message: t('dataSaved', { data: t('headerImage') }),
            });
        } catch {
            toastNotifications.error({
                title: t('failed'),
                message: t('dataNotSaved', { data: t('headerImage') }),
            });
        }
    };

    const showUserEmail = user.email && user.email !== user.userName;
    const metaHeaderSubline = showUserEmail ? user.email : user.userName;

    return (
        <div>
            <MetaHeader headerImage={titleImageSource ?? undefined} onHeaderImageChange={handleTitleImageSelected}>
                <ContentHeader subline={metaHeaderSubline} title={user.displayName ?? ''}>
                    <ContentHeader.Actions>
                        {isActiveDirectoryUser && (
                            <Link key="externalLink" target="_blank" to="https://myaccount.microsoft.com/?ref=MeControl">
                                <Button component="span" variant={variants.button.secondary}>
                                    <span>{t('editCompanyProfile')}</span>
                                    <Icon className="ml-base" icon="edit" />
                                </Button>
                            </Link>
                        )}
                    </ContentHeader.Actions>
                </ContentHeader>

                <Avatar
                    alt={displayName ?? undefined}
                    size="xl"
                    src={imageSource ?? undefined}
                    onImageSelected={!isActiveDirectoryUser ? handleImageSelected : undefined}
                />
                <MetaHeader.ContentSection>
                    {showUserEmail && <MetaHeader.ContentSectionLine icon="badge">{user.userName}</MetaHeader.ContentSectionLine>}
                    {user.mobileNumber && user.mobileNumberSynced && <Anchor variant={variants.link.smartphone}>{user.mobileNumber}</Anchor>}
                    {user.phoneNumber && user.phoneNumberSynced && <Anchor variant={variants.link.phone}>{user.phoneNumber}</Anchor>}
                </MetaHeader.ContentSection>
            </MetaHeader>
        </div>
    );
};
